import React, { useState, useEffect, useContext, useCallback } from 'react';
import Api from '../../../Api';
import * as Sentry from '@sentry/browser';
import MessageContext from '../../../MessageContext';
import classes from './styles.module.css';

// Права с битовыми значениями (должны совпадать с бэкендом)
const PERMISSIONS = {
    TRANSACTION_CANCELLING: 1,
    TRANSACTION_LIMIT_SETTING: 2,
    STATISTICS_ACCESS: 4,
    MAIL_ALERT_SETTING: 8,
    PAYOUT_REGISTRY_PROCESS: 16,
    PAYOUT_REGISTRY_VIEW: 32,
    PAYOUT_REGISTRY_REPORT: 64,
    PAYOUT_REGISTRIES_LIST: 128,
    PREPAID_CARDS_VIEW: 256,
    PREPAID_CARDS_ACTIVATE: 512,
    STATISTICS_REPORT_1C_VIEW: 1024,
    ACCESS_PAYOUTS: 2048,
    ACCESS_METAL: 4096,
    ACCESS_SBP_REGISTRY: 8192,
    ACCESS_SBP_PAYOUT: 16384,
    ACCESS_ANTIFRAUD_FILE: 32768,
    ACCESS_ANTIFRAUD_SINGLE: 65536,
    ACCESS_STATEMENT: 131072,
};

// Названия прав на русском
const PERMISSION_LABELS = {
    TRANSACTION_CANCELLING: 'Отмена операции',
    TRANSACTION_LIMIT_SETTING: 'Лимиты операций',
    STATISTICS_ACCESS: 'Статистика и аналитика',
    MAIL_ALERT_SETTING: 'Email-уведомления',
    PAYOUT_REGISTRY_PROCESS: 'Загрузка реестров',
    PAYOUT_REGISTRY_VIEW: 'Просмотр реестра',
    PAYOUT_REGISTRY_REPORT: 'Выгрузка отчёта',
    PAYOUT_REGISTRIES_LIST: 'Список реестров',
    PREPAID_CARDS_VIEW: 'Предоплаченные карты',
    PREPAID_CARDS_ACTIVATE: 'Активация карт',
    STATISTICS_REPORT_1C_VIEW: 'Выгрузка 1С',
    ACCESS_PAYOUTS: 'Разовые выплаты',
    ACCESS_METAL: 'Драгоценные металлы (ПСА)',
    ACCESS_SBP_REGISTRY: 'Реестры СБП',
    ACCESS_SBP_PAYOUT: 'Выплаты СБП',
    ACCESS_ANTIFRAUD_FILE: 'Антифрод: проверка файла',
    ACCESS_ANTIFRAUD_SINGLE: 'Антифрод: разовая проверка',
    ACCESS_STATEMENT: 'Выписка по балансу',
};

// Описания прав — что именно они открывают в интерфейсе
const PERMISSION_DESCRIPTIONS = {
    TRANSACTION_CANCELLING: 'Кнопка «Отправить» в разделе разовых выплат',
    TRANSACTION_LIMIT_SETTING: 'Настройка лимитов на размер операции (в разработке)',
    STATISTICS_ACCESS: 'Доступ к разделу статистики и аналитических данных',
    MAIL_ALERT_SETTING: 'Настройка уведомлений по электронной почте (в разработке)',
    PAYOUT_REGISTRY_PROCESS: 'Меню «Финансы \u2192 Реестры». Загрузка файлов для массовых выплат',
    PAYOUT_REGISTRY_VIEW: 'Детальный просмотр содержимого реестра и его транзакций',
    PAYOUT_REGISTRY_REPORT: 'Скачивание отчёта по реестру в Excel',
    PAYOUT_REGISTRIES_LIST: 'Страница «Выплаты» со списком всех реестров',
    PREPAID_CARDS_VIEW: 'Меню «Предоплаченные карты». Просмотр списка карт и их статусов',
    PREPAID_CARDS_ACTIVATE: 'Активация предоплаченной карты через заполнение KYC-данных',
    STATISTICS_REPORT_1C_VIEW: 'Виджет выгрузки отчёта в формате 1С на страницах транзакций и выплат',
    ACCESS_PAYOUTS: 'Меню «Финансы \u2192 Разовые выплаты». Создание одиночных выплат',
    ACCESS_METAL: 'Меню «Финансы \u2192 Драгоценные металлы». Управление договорами ПСА',
    ACCESS_SBP_REGISTRY: 'Меню «Мобильные реестры» и «Реестры СБП». Загрузка реестров через СБП',
    ACCESS_SBP_PAYOUT: 'Меню «Финансы \u2192 Выплаты СБП». Создание одиночных выплат через СБП',
    ACCESS_ANTIFRAUD_FILE: 'Меню «Антифрод \u2192 Проверка файла». Пакетная проверка из CSV/XLSX',
    ACCESS_ANTIFRAUD_SINGLE: 'Меню «Антифрод \u2192 Разовая проверка». Проверка одного PAN/телефона/ИНН',
    ACCESS_STATEMENT: 'Меню «Выписка». Просмотр журнала операций по балансу и экспорт в Excel',
};

// Группы прав для каскадных переключателей
const PERMISSION_GROUPS = [
    {
        code: 'TRANSACTIONS',
        label: 'Транзакции',
        permissions: ['TRANSACTION_CANCELLING', 'TRANSACTION_LIMIT_SETTING'],
    },
    {
        code: 'STATISTICS',
        label: 'Статистика',
        permissions: ['STATISTICS_ACCESS', 'STATISTICS_REPORT_1C_VIEW'],
    },
    {
        code: 'PAYOUT_REGISTRY',
        label: 'Реестры выплат',
        permissions: [
            'PAYOUT_REGISTRY_PROCESS',
            'PAYOUT_REGISTRY_VIEW',
            'PAYOUT_REGISTRY_REPORT',
            'PAYOUT_REGISTRIES_LIST',
        ],
    },
    {
        code: 'PREPAID_CARDS',
        label: 'Предоплаченные карты',
        permissions: ['PREPAID_CARDS_VIEW', 'PREPAID_CARDS_ACTIVATE'],
    },
    {
        code: 'SBP',
        label: 'СБП (Система быстрых платежей)',
        permissions: ['ACCESS_SBP_REGISTRY', 'ACCESS_SBP_PAYOUT'],
    },
    {
        code: 'ANTIFRAUD',
        label: 'Антифрод система',
        permissions: ['ACCESS_ANTIFRAUD_FILE', 'ACCESS_ANTIFRAUD_SINGLE'],
    },
    {
        code: 'BALANCE',
        label: 'Баланс и выписка',
        permissions: ['ACCESS_STATEMENT'],
    },
    {
        code: 'OTHER',
        label: 'Прочее',
        permissions: ['MAIL_ALERT_SETTING', 'ACCESS_PAYOUTS', 'ACCESS_METAL'],
    },
];

function PermissionsEditor({
    token,
    accountId,
    merchantUuid,
    onClose,
    onSaved,
    isGlobal,
    readOnly,
    readOnlyMessage,
    initialPrivileges,
}) {
    const { onMessage } = useContext(MessageContext);
    const [privileges, setPrivileges] = useState(initialPrivileges || 0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    // Загрузка текущих прав
    useEffect(() => {
        if (!accountId) return;

        if (isGlobal) {
            // Глобальные права: загрузка из /account/{id}/global-permissions
            setIsLoading(true);
            new Api({ token })
                .getGlobalPermissions(accountId)
                .then((data) => {
                    setPrivileges(data.privileges || 0);
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    console.error('Failed to load global permissions:', err);
                    onMessage({ type: 'error', message: 'Не удалось загрузить глобальные права' });
                })
                .finally(() => setIsLoading(false));
        } else if (merchantUuid) {
            // Per-merchant права
            setIsLoading(true);
            new Api({ token })
                .getAccountMerchantPermissions(accountId, merchantUuid)
                .then((data) => {
                    setPrivileges(data.privileges || 0);
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    console.error('Failed to load permissions:', err);
                    onMessage({ type: 'error', message: 'Не удалось загрузить права' });
                })
                .finally(() => setIsLoading(false));
        } else {
            // Если нет merchantUuid и не isGlobal - используем initialPrivileges
            setPrivileges(initialPrivileges || 0);
            setIsLoading(false);
        }
    }, [token, accountId, merchantUuid, isGlobal, onMessage, initialPrivileges]);

    // Проверка, включено ли право
    const hasPermission = useCallback(
        (permCode) => {
            const bit = PERMISSIONS[permCode];
            return (privileges & bit) === bit;
        },
        [privileges]
    );

    // Проверка состояния группы: all, some, none
    const getGroupState = useCallback(
        (group) => {
            const enabledCount = group.permissions.filter((p) => hasPermission(p)).length;
            if (enabledCount === 0) return 'none';
            if (enabledCount === group.permissions.length) return 'all';
            return 'some';
        },
        [hasPermission]
    );

    // Переключение одного права
    const togglePermission = (permCode) => {
        if (readOnly) return;
        const bit = PERMISSIONS[permCode];
        setPrivileges((prev) => ((prev & bit) === bit ? prev & ~bit : prev | bit));
    };

    // Переключение всей группы (каскад)
    const toggleGroup = (group) => {
        if (readOnly) return;
        const state = getGroupState(group);
        let newPriv = privileges;

        if (state === 'all') {
            for (const p of group.permissions) {
                newPriv &= ~PERMISSIONS[p];
            }
        } else {
            for (const p of group.permissions) {
                newPriv |= PERMISSIONS[p];
            }
        }

        setPrivileges(newPriv);
    };

    // Развернуть/свернуть группу
    const toggleExpand = (groupCode) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupCode]: !prev[groupCode],
        }));
    };

    // Сохранение
    const savePermissions = async () => {
        if (readOnly) return;
        setIsSaving(true);
        try {
            if (isGlobal) {
                await new Api({ token }).updateGlobalPermissions(accountId, true, privileges);
                onMessage({ type: 'success', message: 'Глобальные права сохранены' });
            } else {
                await new Api({ token }).updateAccountMerchantPermissions(accountId, merchantUuid, privileges);
                onMessage({ type: 'success', message: 'Права успешно сохранены' });
            }
            if (onSaved) onSaved(privileges);
            if (onClose) onClose();
        } catch (err) {
            console.error('Failed to save permissions:', err);
            onMessage({ type: 'error', message: 'Не удалось сохранить права' });
        } finally {
            setIsSaving(false);
        }
    };

    // Выбрать все / снять все
    const selectAll = () => {
        if (readOnly) return;
        let allBits = 0;
        for (const bit of Object.values(PERMISSIONS)) {
            allBits |= bit;
        }
        setPrivileges(allBits);
    };

    const clearAll = () => {
        if (readOnly) return;
        setPrivileges(0);
    };

    if (isLoading) {
        return (
            <div className={classes.Container}>
                <div className={classes.Loading}>Загрузка прав...</div>
            </div>
        );
    }

    const title = isGlobal
        ? 'Глобальные права пользователя'
        : readOnly
          ? 'Права пользователя на проекте (только чтение)'
          : 'Права пользователя на проекте';

    return (
        <div className={classes.Container}>
            <div className={classes.Header}>
                <h4 className={classes.Title}>{title}</h4>
                {!readOnly && (
                    <div className={classes.QuickActions}>
                        <button type="button" className={classes.BtnLink} onClick={selectAll}>
                            Выбрать все
                        </button>
                        <span className={classes.Separator}>|</span>
                        <button type="button" className={classes.BtnLink} onClick={clearAll}>
                            Снять все
                        </button>
                    </div>
                )}
            </div>

            {readOnly && readOnlyMessage && <div className={classes.ReadOnlyBanner}>{readOnlyMessage}</div>}

            <div className={classes.GroupsList}>
                {PERMISSION_GROUPS.map((group) => {
                    const state = getGroupState(group);
                    const isExpanded = expandedGroups[group.code] !== false; // по умолчанию развернуты

                    return (
                        <div key={group.code} className={classes.Group}>
                            <div className={classes.GroupHeader} onClick={() => toggleExpand(group.code)}>
                                <label className={classes.GroupToggle} onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={state === 'all'}
                                        ref={(el) => {
                                            if (el) el.indeterminate = state === 'some';
                                        }}
                                        onChange={() => toggleGroup(group)}
                                        disabled={readOnly}
                                    />
                                    <span className={classes.Checkbox}></span>
                                </label>
                                <span className={classes.GroupLabel}>{group.label}</span>
                                <span className={classes.GroupCount}>
                                    ({group.permissions.filter((p) => hasPermission(p)).length}/
                                    {group.permissions.length})
                                </span>
                                <span className={`${classes.ExpandIcon} ${isExpanded ? classes.Expanded : ''}`}>
                                    &#9660;
                                </span>
                            </div>

                            {isExpanded && (
                                <div className={classes.PermissionsList}>
                                    {group.permissions.map((permCode) => (
                                        <label
                                            key={permCode}
                                            className={`${classes.PermissionItem} ${readOnly ? classes.PermissionItemReadOnly : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={hasPermission(permCode)}
                                                onChange={() => togglePermission(permCode)}
                                                disabled={readOnly}
                                            />
                                            <span className={classes.Checkbox}></span>
                                            <div className={classes.PermissionText}>
                                                <span className={classes.PermissionLabel}>
                                                    {PERMISSION_LABELS[permCode] || permCode}
                                                </span>
                                                {PERMISSION_DESCRIPTIONS[permCode] && (
                                                    <span className={classes.PermissionDescription}>
                                                        {PERMISSION_DESCRIPTIONS[permCode]}
                                                    </span>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className={classes.Footer}>
                {!readOnly && (
                    <button type="button" className={classes.BtnPrimary} onClick={savePermissions} disabled={isSaving}>
                        {isSaving ? 'Сохранение...' : 'Сохранить права'}
                    </button>
                )}
                {onClose && (
                    <button type="button" className={classes.BtnSecondary} onClick={onClose} disabled={isSaving}>
                        {readOnly ? 'Закрыть' : 'Отмена'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default PermissionsEditor;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/PermissionsEditor/index.js